<template>
    <div id="config-users">
        <FilterView placeholder="Chercher un utilisateur..." v-model="searchTerm">
            <CustomButton
                v-if="selected.length"
                icon="trash-alt"
                class="default red-theme"
                @click="showConfirmMany = true"
            >
                Supprimer
            </CustomButton>
            <CustomButton icon="plus" class="default green-theme" @click="handleNew">
                Ajouter un utilisateur
            </CustomButton>
        </FilterView>
        <Modal
            v-if="showEdit"
            v-model="user"
            :groups="groups"
            :types="types"
            :roles="roles"
            :footprints="footprints"
            @dismiss="showEdit = false"
            @close="handleClose"
        />
        <Confirm v-if="showConfirmOne" @dismiss="showConfirmOne = false" @close="handleDeleteOne">
            <span slot="header">Supprimer l'utilisateur</span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer l'utilisateur
                {{ `${user.first_name} ${user.last_name}` }} ?
            </span>
        </Confirm>
        <Confirm
            v-if="showConfirmMany"
            @dismiss="showConfirmMany = false"
            @close="handleDeleteMany"
        >
            <span slot="header">
                Supprimer {{ selected.length }}
                {{ selected.length > 1 ? 'utilisateurs' : 'utilisateur' }}
            </span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer les utilisateurs sélectionnés ?
            </span>
        </Confirm>
        <h2 class="title">Utilisateurs</h2>
        <LoadingDots v-if="isLoading" />
        <CardPager>
            <UserCard
                v-for="item in filtered"
                :item="item"
                :key="item.id"
                :handleEdit="handleEdit"
                :handleConfirm="handleConfirm"
                :selected="selected"
                @selected="handleSelect"
            />
        </CardPager>
    </div>
</template>

<script>
import FilterView from '@/components/config/Filter';
import Modal from '@/components/config/ModalUser';
import Confirm from '@/components/config/DeleteConfirm';
import CustomButton from '@/components/IconButton';
import LoadingDots from '@/components/LoadingDots';
import UserCard from '@/components/config/UserCard';
import CardPager from '@/components/CardPager';

export default {
    data() {
        return {
            showEdit: false,
            showConfirmOne: false,
            showConfirmMany: false,
            types: [],
            roles: [],
            groups: [],
            footprints: [],
            users: [],
            user: null,
            selected: [],
            searchTerm: null,
            isLoading: true,
        };
    },
    watch: {
        searchTerm() {
            this.selected.splice(0);
        },
    },
    computed: {
        filtered() {
            if (this.searchTerm) {
                const re = new RegExp(this.searchTerm, 'i');
                return this.users.filter((u) => re.test(u.last_name) || re.test(u.first_name));
            }
            return this.users;
        },
    },
    mounted() {
        Promise.all([
            this.$http.get('/api/business-group'),
            this.$http.get('/api/user'),
            this.$http.get('/api/user-type'),
            this.$http.get('/api/user-role'),
            this.$http.get('/api/footprint'),
        ]).then(
            (responses) => {
                // success callback
                this.groups = responses[0].body.map((g) => ({
                    id: g.id,
                    text: g.name,
                }));
                this.users = responses[1].body;
                this.types = responses[2].body.map((t) => ({
                    ...t,
                    text: t.desc,
                }));
                this.roles = responses[3].body.map((r) => ({
                    ...r,
                    text: r.desc,
                }));
                this.footprints = responses[4].body.map((f) => ({
                    id: f.id,
                    text: f.name,
                }));
                this.isLoading = false;
            },
            (responses) => {
                // error callback
                console.log(JSON.stringify(responses));
                this.isLoading = false;
            },
        );
    },
    methods: {
        _getUser(id) {
            const user = this.users.find((u) => u.id === id);
            return {
                ...user,
                groups: user.groups.map((g) => ({
                    id: g.id,
                    text: g.name,
                })),
                type: {
                    ...user.type,
                    // id: user.type.id,
                    text: user.type.desc,
                },
                role: {
                    id: user.role.id,
                    text: user.role.desc,
                },
                footprints: user.footprints.map((f) => ({
                    id: f.id,
                    text: f.name,
                })),
            };
        },
        loadUsers() {
            this.$http
                .get('/api/user')
                .then(
                    (response) => {
                        // success callback
                        this.users = response.body;
                    },
                    (responses) => {
                        // error callback
                        console.log(JSON.stringify(responses));
                    },
                )
                .finally(() => (this.isLoading = false));
        },
        handleSelect(event) {
            const { id, checked } = event;
            const index = this.selected.findIndex((s) => s === id);
            if (checked) {
                if (index === -1) {
                    this.selected.push(id);
                }
            } else {
                if (index > -1) {
                    this.selected.splice(index, 1);
                }
            }
        },
        handleConfirm(item) {
            this.user = this._getUser(item);
            this.showConfirmOne = true;
        },
        handleEdit(item) {
            this.user = this._getUser(item);
            this.showEdit = true;
        },
        handleNew() {
            this.user = {
                id: null,
                login: '',
                first_name: '',
                last_name: '',
                email: '',
                password: '',
                role: {},
                type: {},
                groups: [],
                footprints: [],
            };
            this.showEdit = true;
        },
        handleClose() {
            // Crée le nouvel utilisateur, si le login de l'utilisateur est renseigné (+ autres)
            if (
                !this.user.id &&
                this.user.type &&
                this.user.login &&
                this.user.password &&
                this.user.role
            ) {
                this.$http.post('/api/user', this.user).then(() => {
                    this.showEdit = false;
                    this.user = null;
                    this.loadUsers();
                });
                // Met à jour l'utilisateur, si le login de l'utilisateur est renseigné + rôle (ne pas tout mettre pour autoriser l'édition des users LDAP)
            } else if (this.user.id && this.user.role) {
                this.$http.put(`/api/user/${this.user.id}`, this.user).then(() => {
                    this.showEdit = false;
                    this.user = null;
                    this.loadUsers();
                });
            }
        },
        handleDeleteOne() {
            this.$http
                .delete(`/api/user/${this.user.id}`)
                .then(() => {
                    const index = this.users.findIndex((u) => u.id === this.user.id);
                    this.users.splice(index, 1);
                    this.showConfirmOne = false;
                })
                .catch();
        },
        handleDeleteMany() {
            const requests = this.selected.map((s) => this.$http.delete(`/api/user/${s}`));
            Promise.all(requests)
                .then(() => {
                    this.showConfirmMany = false;
                    this.selected.splice(0);
                    this.isLoading = true;
                    return this.$http.get('/api/user');
                })
                .then((response) => {
                    // success callback
                    this.users = response.body;
                    this.isLoading = false;
                })
                .catch(() => {
                    this.isLoading = false;
                });
        },
    },
    components: {
        Modal,
        Confirm,
        FilterView,
        CustomButton,
        LoadingDots,
        UserCard,
        CardPager,
    },
};
</script>

<style lang="scss"></style>
